/**
 * Job Duplication Hook
 * 
 * Provides functionality to create a complete copy of an existing job,
 * including all specifications, delivery details, and related data. The
 * copied job is created in draft status with " - Copy" appended to the
 * title for easy identification.
 * 
 * **Copy Process:**
 * 1. Fetch source job and verify access permissions
 * 2. Fetch all job specifications (with ordering)
 * 3. Fetch all delivery locations
 * 4. Create new job in draft status
 * 5. Copy specifications maintaining order
 * 6. Copy delivery details with all fields
 * 7. Link copied job to source via copied_from_job_id
 * 
 * **What Gets Copied:**
 * - Job title (with " - Copy" suffix)
 * - Job description and category
 * - Budget and dates (art ready, due date, etc.)
 * - Project name and internal reference
 * - Priority and procurement method
 * - Client notes
 * - All specifications (name, type, content, sections, order)
 * - All delivery locations (address, contacts, requirements)
 * - Template references (for both specs and locations)
 * 
 * **What Doesn't Get Copied:**
 * - Status (new job starts as 'draft')
 * - RFQs and bids
 * - Awards and purchase orders
 * - Milestones and completions
 * - Messages and activity logs
 * - Payment transactions
 * - Reviews and ratings
 * 
 * **Access Control:**
 * - User must be authenticated
 * - User must be in same organization as source job
 * - Source job must not be deleted (is_deleted = false)
 * 
 * **Key Features:**
 * - Complete job duplication
 * - Maintains specification order
 * - Preserves delivery sequence
 * - Template tracking for both specs and locations
 * - Graceful error handling with partial copy notifications
 * - Loading state management
 * - Detailed console logging for debugging
 * 
 * **Use Cases:**
 * - Creating similar jobs quickly
 * - Job templates without formal template system
 * - Testing job variations
 * - Recurring job orders
 * - Job versioning and iteration
 * 
 * @module hooks/useCopyJob
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for duplicating jobs with all their specifications and delivery details
 * 
 * Creates a complete copy of an existing job with all related data, maintaining
 * proper ordering and structure. The copy is created in draft status and linked
 * to the original via copied_from_job_id.
 * 
 * @returns {Object} Job copy functions and state
 * @returns {Function} copyJob - Function to duplicate a job
 * @returns {boolean} loading - Upload/copy in progress
 * 
 * @example
 * // Copy a job and navigate to the new draft
 * const { copyJob, loading } = useCopyJob();
 * 
 * const handleDuplicate = async (sourceJobId: string) => {
 *   try {
 *     const newJobPublicId = await copyJob(sourceJobId);
 *     toast({ 
 *       title: 'Job Copied', 
 *       description: 'Your job has been duplicated as a draft' 
 *     });
 *     navigate(`/jobs/${newJobPublicId}/edit`);
 *   } catch (error) {
 *     toast({ 
 *       title: 'Copy Failed', 
 *       description: error.message,
 *       variant: 'destructive' 
 *     });
 *   }
 * };
 * 
 * @example
 * // Quick copy button in job list
 * <Button 
 *   onClick={() => handleDuplicate(job.id)} 
 *   disabled={loading}
 * >
 *   {loading ? <Spinner /> : <Copy />} Copy Job
 * </Button>
 * 
 * @example
 * // Copy with user confirmation
 * const { copyJob } = useCopyJob();
 * 
 * const handleCopyWithConfirm = async () => {
 *   const confirmed = await showConfirmDialog({
 *     title: 'Duplicate Job',
 *     message: 'Create a copy of this job as a new draft?'
 *   });
 *   
 *   if (confirmed) {
 *     const newId = await copyJob(originalJobId);
 *     // Automatically open editor for new draft
 *     navigate(`/jobs/${newId}/edit`);
 *   }
 * };
 */
export const useCopyJob = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  /**
   * Copy a job with all its specifications and delivery details
   * 
   * @param sourceJobId - The UUID (internal ID) of the job to copy
   * @returns Promise resolving to the public_id of the newly created job
   * @throws Error if user is not authenticated, job not found, or copy fails
   */
  const copyJob = async (sourceJobId: string): Promise<string> => {
    setLoading(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to copy a job');
      }

      // Get user's profile to retrieve organization_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Failed to retrieve user profile');
      }

      // Fetch the source job
      const { data: sourceJob, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', sourceJobId)
        .eq('is_deleted', false)
        .single();

      if (jobError || !sourceJob) {
        throw new Error('Source job not found');
      }

      // Verify user has access to this job
      if (sourceJob.organization_id !== profile.organization_id) {
        throw new Error('You do not have permission to copy this job');
      }

      // Fetch job specifications
      const { data: specifications, error: specsError } = await supabase
        .from('job_specifications')
        .select('*')
        .eq('job_id', sourceJobId)
        .order('specification_order');

      console.log('Fetched specifications:', specifications?.length || 0, specifications);
      if (specsError) {
        console.error('Failed to fetch specifications:', specsError);
      }

      // Fetch delivery details
      const { data: deliveryDetails, error: deliveryError } = await supabase
        .from('job_delivery_details')
        .select('*')
        .eq('job_id', sourceJobId)
        .order('delivery_order');

      console.log('Fetched delivery details:', deliveryDetails?.length || 0, deliveryDetails);
      if (deliveryError) {
        console.error('Failed to fetch delivery details:', deliveryError);
      }

      // Create new job with modified title and draft status
      const { data: newJob, error: createError } = await supabase
        .from('jobs')
        .insert({
          title: `${sourceJob.title} - Copy`,
          description: sourceJob.description,
          category: sourceJob.category,
          budget_cents: sourceJob.budget_cents,
          art_files_ready_date: sourceJob.art_files_ready_date,
          due_date: sourceJob.due_date,
          project_name: sourceJob.project_name,
          internal_reference: sourceJob.internal_reference,
          priority_level: sourceJob.priority_level,
          procurement_method: sourceJob.procurement_method,
          client_notes: sourceJob.client_notes,
          estimated_start_date: sourceJob.estimated_start_date,
          estimated_completion_date: sourceJob.estimated_completion_date,
          specifications_required: sourceJob.specifications_required,
          delivery_details_required: sourceJob.delivery_details_required,
          status: 'draft',
          workflow_stage: 'draft',
          requirements_complete: false,
          organization_id: profile.organization_id,
          created_by_user_id: user.id,
          copied_from_job_id: sourceJobId,
        })
        .select()
        .single();

      if (createError || !newJob) {
        throw new Error('Failed to create new job');
      }

      // Copy specifications if they exist
      if (specifications && specifications.length > 0) {
        console.log('Copying', specifications.length, 'specifications to new job:', newJob.id);
        const newSpecs = specifications.map(spec => ({
          job_id: newJob.id,
          specification_name: spec.specification_name,
          specification_type: spec.specification_type,
          content: spec.content,
          sections: spec.sections,
          specification_order: spec.specification_order,
          compliance_level: spec.compliance_level,
          custom_requirements: spec.custom_requirements,
          is_mandatory: spec.is_mandatory,
          source_template_id: spec.source_template_id,
          created_by_user_id: user.id,
          last_updated_by_user_id: user.id,
        }));

        console.log('Inserting specifications:', newSpecs);
        const { data: insertedSpecs, error: specsInsertError } = await supabase
          .from('job_specifications')
          .insert(newSpecs)
          .select();

        if (specsInsertError) {
          console.error('Failed to copy specifications:', specsInsertError);
          toast({
            title: "Partial Copy",
            description: "Job created but specifications could not be copied",
            variant: "destructive",
          });
        } else {
          console.log('Successfully copied specifications:', insertedSpecs?.length);
        }
      } else {
        console.log('No specifications to copy');
      }

      // Copy delivery details if they exist
      if (deliveryDetails && deliveryDetails.length > 0) {
        console.log('Copying', deliveryDetails.length, 'delivery details to new job:', newJob.id);
        const newDeliveryDetails = deliveryDetails.map(detail => ({
          job_id: newJob.id,
          delivery_name: detail.delivery_name,
          location_type: detail.location_type,
          address_line1: detail.address_line1,
          address_line2: detail.address_line2,
          city: detail.city,
          state_province: detail.state_province,
          postal_code: detail.postal_code,
          country: detail.country,
          contact_name: detail.contact_name,
          contact_phone: detail.contact_phone,
          contact_email: detail.contact_email,
          special_instructions: detail.special_instructions,
          delivery_window_start: detail.delivery_window_start,
          delivery_window_end: detail.delivery_window_end,
          preferred_delivery_date: detail.preferred_delivery_date,
          delivery_hours: detail.delivery_hours,
          preferred_carriers: detail.preferred_carriers,
          restricted_carriers: detail.restricted_carriers,
          signature_required: detail.signature_required,
          id_required: detail.id_required,
          appointment_required: detail.appointment_required,
          access_requirements: detail.access_requirements,
          dock_information: detail.dock_information,
          latitude: detail.latitude,
          longitude: detail.longitude,
          geocode_precision: detail.geocode_precision,
          deadline_type: detail.deadline_type,
          delivery_order: detail.delivery_order,
          source_template_id: detail.source_template_id,
          created_by_user_id: user.id,
          last_updated_by_user_id: user.id,
        }));

        console.log('Inserting delivery details:', newDeliveryDetails);
        const { data: insertedDelivery, error: deliveryInsertError } = await supabase
          .from('job_delivery_details')
          .insert(newDeliveryDetails)
          .select();

        if (deliveryInsertError) {
          console.error('Failed to copy delivery details:', deliveryInsertError);
          toast({
            title: "Partial Copy",
            description: "Job created but delivery details could not be copied",
            variant: "destructive",
          });
        } else {
          console.log('Successfully copied delivery details:', insertedDelivery?.length);
        }
      } else {
        console.log('No delivery details to copy');
      }

      return newJob.public_id;
    } catch (error) {
      console.error('Error copying job:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { copyJob, loading };
};
